'use client';

import { useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { CommandMenu, type CommandMenuItem } from '@/platform/layout/CommandMenu';
import { policyErrorMessage } from '@/platform/youtube/policyMessages';
import { useComposePolicy } from '@/platform/youtube/useComposePolicy';
import {
  deleteForumComment,
  deleteForumPost,
  patchForumComment,
  patchForumPost,
  type ForumComment,
  type ForumPost,
} from './api';
import { forumCommentUrl, forumPostUrl, reportForumContent } from './forumContentLinks';

type PostProps = {
  kind: 'post';
  post: ForumPost;
  onUpdate?: () => void;
  onDeleted?: () => void;
  allowTitleEdit?: boolean;
};

type CommentProps = {
  kind: 'comment';
  postId: string;
  comment: ForumComment;
  onUpdate?: () => void;
};

type Props = (PostProps | CommentProps) & {
  className?: string;
  onEditingChange?: (editing: boolean) => void;
};

export function ForumCommandMenu(props: Props) {
  const { user, isAuthenticated } = useAuth();
  const { validateTexts } = useComposePolicy();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const isOwner =
    isAuthenticated &&
    user?.uid &&
    (props.kind === 'post' ? props.post.authorId === user.uid : props.comment.authorId === user.uid);

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setStatus('Link copied');
    } catch {
      setStatus('Could not copy link');
    }
  }

  async function report(url: string, label: string, excerpt: string) {
    if (!isAuthenticated) return;
    setError('');
    try {
      if (props.kind === 'post') {
        await reportForumContent({
          targetLabel: label,
          url,
          excerpt,
          targetType: 'forum_post',
          targetId: props.post.id,
        });
      } else {
        await reportForumContent({
          targetLabel: label,
          url,
          excerpt,
          targetType: 'forum_comment',
          targetId: props.comment.id,
          parentId: props.postId,
        });
      }
      setStatus('Report submitted — thank you');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Report failed');
    }
  }

  function startEdit() {
    if (props.kind === 'post') {
      setDraftTitle(props.post.title);
      setDraft(props.post.body);
    } else {
      setDraft(props.comment.body);
    }
    setEditing(true);
    props.onEditingChange?.(true);
    setError('');
    setStatus('');
  }

  async function saveEdit() {
    const texts = props.kind === 'post' ? [draftTitle, draft] : [draft];
    const policyErr = validateTexts(texts);
    if (policyErr) {
      setError(policyErr);
      return;
    }
    setError('');
    try {
      if (props.kind === 'post') {
        const allowTitle = props.allowTitleEdit !== false && !props.post.artifactType;
        await patchForumPost(props.post.id, {
          ...(allowTitle ? { title: draftTitle.trim() } : {}),
          body: draft.trim(),
        });
      } else {
        await patchForumComment(props.postId, props.comment.id, draft.trim());
      }
      setEditing(false);
      props.onEditingChange?.(false);
      setStatus('Saved');
      props.onUpdate?.();
    } catch (err) {
      setError(policyErrorMessage(err) ?? (err instanceof Error ? err.message : 'Save failed'));
    }
  }

  async function confirmDelete() {
    const message =
      props.kind === 'post'
        ? 'Delete this post permanently? This cannot be undone.'
        : 'Delete this reply permanently? This cannot be undone.';
    if (!window.confirm(message)) return;
    setError('');
    try {
      if (props.kind === 'post') {
        if (props.post.artifactType) {
          setError('This discussion is tied to a haul report and cannot be deleted.');
          return;
        }
        await deleteForumPost(props.post.id);
        if (props.onDeleted) {
          props.onDeleted();
        } else {
          props.onUpdate?.();
        }
      } else {
        await deleteForumComment(props.postId, props.comment.id);
        props.onUpdate?.();
      }
    } catch (err) {
      setError(policyErrorMessage(err) ?? (err instanceof Error ? err.message : 'Delete failed'));
    }
  }

  const menuItems: CommandMenuItem[] = [];
  const postUrl =
    props.kind === 'post' ? forumPostUrl(props.post.id) : forumCommentUrl(props.postId, props.comment.id);

  menuItems.push({
    id: 'copy-link',
    label: 'Copy link',
    onClick: () => void copyLink(postUrl),
  });

  if (isOwner) {
    menuItems.push({
      id: 'edit',
      label: 'Edit',
      onClick: startEdit,
    });
    if (props.kind === 'comment' || !props.post.artifactType) {
      menuItems.push({
        id: 'delete',
        label: props.kind === 'post' ? 'Delete post' : 'Delete reply',
        onClick: () => void confirmDelete(),
        destructive: true,
      });
    }
  } else if (isAuthenticated) {
    const label = props.kind === 'post' ? 'forum post' : 'forum reply';
    const excerpt = props.kind === 'post' ? props.post.body : props.comment.body;
    menuItems.push({
      id: 'report',
      label: 'Report abuse',
      onClick: () => void report(postUrl, label, excerpt),
      destructive: true,
    });
  }

  const allowTitleEdit =
    props.kind === 'post' && props.allowTitleEdit !== false && !props.post.artifactType;

  return (
    <div className="forum-command-menu">
      <CommandMenu
        items={menuItems}
        ariaLabel={props.kind === 'post' ? 'Post options' : 'Reply options'}
        className={props.className}
      />
      {editing && (
        <div className="forum-command-menu__edit" role="form">
          {allowTitleEdit && (
            <label>
              Title
              <input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} required />
            </label>
          )}
          <label>
            {props.kind === 'post' ? 'Post' : 'Reply'}
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} required rows={4} />
          </label>
          <div className="forum-command-menu__edit-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => void saveEdit()}>
              Save
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setEditing(false);
                props.onEditingChange?.(false);
                setError('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <p className="form-error forum-command-menu__message">{error}</p>}
      {status && !error && <p className="text-muted forum-command-menu__message">{status}</p>}
    </div>
  );
}
